import { computed, defineComponent } from 'vue'
import type { IAvatarProps } from './types'
import { tv } from 'tailwind-variants'
import { isFunction } from 'math'

const getInitials = (name: string): string => {
  const parts = name.split(' ')
  if (parts.length <= 0) return ''
  if (parts.length <= 1) return parts[0][0]
  return [parts[0][0], parts[1][0]].join('')
}

const AvatarClass = tv({
  base: 'text-white flex items-center justify-center border border-gray-200 overflow-hidden',
  variants: {
    round: {
      full: 'rounded-full',
      md: 'rounded-md',
      sm: 'rounded-[1.12px]',
      none: 'rounded-none'
    },
    circle: {
      true: 'border-radius-[100%] w-8 h-8 bg-red-500 display-none'
    },
    border: {
      false: 'border-none'
    },
    size: {
      m: 'w-10 h-10',
      l: 'w-32 h-32',
      s: 'w-8 h-8 text-xs',
      xs: 'w-8 h-8 text-[0.5rem]',
      xxs: 'w-[18px] h-[18px] text-[0.5rem]',
      xxs2: 'w-4 h-4 text-[0.45rem]',
      full: 'w-full h-full'
    }
  },
  defaultVariants: {
    round: 'md',
    size: 'm',
  }
})

const Avatar = defineComponent<IAvatarProps>({
  setup(props) {
    return () => {
      const fullname = computed(() => props.fullname || '')
      const bgColor = computed(() => props.colorPreference || '#2d3436')
      const color = computed(() => props.color || '#FFFFFF')
      const initials = computed(() => getInitials(fullname.value))

      if (props.src)
        return (
          <div
            class={AvatarClass({
              size: props.size as any,
              round: props.round as any
            })}
            style={{
              userSelect: 'none',
              cursor: 'pointer',
              ...(props.circle
                ? {
                    borderRadius: '100%',
                    width: '2rem',
                    height: '2rem'
                  }
                : {}),
              ...(isFunction(props.src) ? {
                backgroundColor: bgColor.value
              } : {})
            }}
          >
            {isFunction(props.src) ? (
              <props.src />
            ) : (
              <img
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'cover',
                  aspectRatio: '1 / 1'
                }}
                src={props.src}
              />
            )}
          </div>
        )

      return (
        <div
          class={AvatarClass({
            size: props.size as any,
            round: props.round as any,
            circle: !!props.circle
          })}
          style={{
            backgroundColor: bgColor.value,
            color: color.value,
            userSelect: 'none',
            cursor: 'pointer',
            ...(props.circle
              ? {
                  borderRadius: '100%',
                  width: '2rem',
                  height: '2rem'
                }
              : {})
          }}
        >
          {(initials.value || 'svamp').toUpperCase()}
        </div>
      )
    }
  }
})

Avatar.props = ['fullname', 'colorPreference', 'color',  'src', 'size', 'round', 'circle']

export default Avatar
